import http from '@/api/httpRequest.js';
import localForage from 'localforage';
import { MESSAGE_STATUS, MESSAGE_TYPE } from "../api/enums.js";
import { chatMatchesKeyword, chatSearchKey, messagesMatchKeyword } from '@/utils/chatSearch.js';
import userStore from './userStore';

/* 为了加速拉取离线消息效率，拉取时消息暂时存储到cacheChats,等
待所有离线消息拉取完成后，再统一放至vuex中进行渲染*/
let cacheChats = [];
let chatByKey = new Map();
const persistTasks = new Map();
let activateSeq = 0;

const INACTIVE_KEEP_MESSAGES = 8;
const INACTIVE_TRIM_AT = 24;
const MAX_ACTIVE_MESSAGES = 400;
const MAX_MEMORY_MESSAGES = 1200;
const LOAD_BATCH = 40;

function chatLookupKey(type, targetId) {
	return String(type) + ':' + String(targetId);
}

function normalizeHiddenId(id) {
	if (id == null || id === '') {
		return '';
	}
	return String(id);
}

function isHiddenPrivateId(state, id) {
	const key = normalizeHiddenId(id);
	if (!key) {
		return false;
	}
	const list = state.hiddenPrivateIds || [];
	for (let i = 0; i < list.length; i++) {
		if (String(list[i]) === key) {
			return true;
		}
	}
	return false;
}

function shouldSkipPrivateChat(store, state, targetId, allowActiveFriend) {
	if (isHiddenPrivateId(state, targetId)) {
		if (allowActiveFriend && store.getters.isFriend(targetId)) {
			return false;
		}
		return true;
	}
	if (allowActiveFriend) {
		return false;
	}
	const friendStore = store.state.friendStore;
	if (!friendStore || friendStore.friendsVersion < 1) {
		return false;
	}
	return !store.getters.isFriend(targetId);
}

function storageChatKey(userId, chat) {
	return `chats-${userId}-${chat.type}-${chat.targetId}`;
}

function registerChat(chat) {
	if (!chat) {
		return;
	}
	chatByKey.set(chatLookupKey(chat.type, chat.targetId), chat);
}

function unregisterChat(chat) {
	if (!chat) {
		return;
	}
	chatByKey.delete(chatLookupKey(chat.type, chat.targetId));
}

function rebuildChatIndex(chats) {
	chatByKey = new Map();
	if (!Array.isArray(chats)) {
		return;
	}
	for (let i = 0; i < chats.length; i++) {
		registerChat(chats[i]);
	}
}

function lookupChat(chatInfo) {
	if (!chatInfo) {
		return;
	}
	return chatByKey.get(chatLookupKey(chatInfo.type, chatInfo.targetId));
}

function sameParticipant(id, myId, targetId) {
	if (id == null || id === '') {
		return true;
	}
	const value = String(id);
	return value === String(myId) || value === String(targetId);
}

function privateMessageBelongs(chat, msg) {
	if (!chat || chat.type !== 'PRIVATE' || !msg) {
		return true;
	}
	if (msg.type === MESSAGE_TYPE.TIP_TEXT || msg.type === MESSAGE_TYPE.TIP_TIME) {
		return true;
	}
	const myId = userStore.state.userInfo && userStore.state.userInfo.id;
	if (myId == null || myId === '') {
		return true;
	}
	return sameParticipant(msg.sendId, myId, chat.targetId) && sameParticipant(msg.recvId, myId, chat.targetId);
}

function dropForeignPrivateMessages(chat, messages) {
	if (!chat || chat.type !== 'PRIVATE' || !Array.isArray(messages)) {
		return messages || [];
	}
	const kept = [];
	let removed = false;
	for (let i = 0; i < messages.length; i++) {
		const msg = messages[i];
		if (privateMessageBelongs(chat, msg)) {
			kept.push(msg);
		} else {
			removed = true;
		}
	}
	return removed ? kept : messages;
}

function freezeChatMessages(chat) {
	if (!chat || !Array.isArray(chat.messages) || Object.isFrozen(chat.messages)) {
		return;
	}
	for (let i = 0; i < chat.messages.length; i++) {
		const msg = chat.messages[i];
		if (msg && typeof msg === 'object' && !Object.isFrozen(msg)) {
			Object.freeze(msg);
		}
	}
	chat.messages = Object.freeze(chat.messages);
}

function ensureMutableMessages(chat) {
	if (!chat) {
		return;
	}
	if (!Array.isArray(chat.messages)) {
		chat.messages = [];
		return;
	}
	if (Object.isFrozen(chat.messages)) {
		chat.messages = chat.messages.map((msg) => {
			if (msg && typeof msg === 'object' && Object.isFrozen(msg)) {
				return Object.assign({}, msg);
			}
			return msg;
		});
	}
}

function cloneMessage(msg) {
	if (!msg || typeof msg !== 'object') {
		return msg;
	}
	return Object.assign({}, msg);
}

function markLoadingFail(chat) {
	if (!chat || !Array.isArray(chat.messages)) {
		return;
	}
	let mutated = false;
	for (let i = 0; i < chat.messages.length; i++) {
		if (chat.messages[i] && chat.messages[i].loadStatus == "loading") {
			if (!mutated) {
				ensureMutableMessages(chat);
				mutated = true;
			}
			chat.messages[i].loadStatus = "fail";
		}
	}
}

function trimChatMessages(chat, keep) {
	if (!chat) {
		return;
	}
	const keepCount = keep == null ? INACTIVE_KEEP_MESSAGES : keep;
	if (!Array.isArray(chat.messages)) {
		chat.messages = [];
		chat.messagesHydrated = false;
		return;
	}
	if (chat.messages.length > keepCount) {
		chat.messages = chat.messages.slice(-keepCount);
		chat.messagesTrimmed = true;
	}
	chat.messagesHydrated = false;
	freezeChatMessages(chat);
}

function trimInactiveChats(chats, activeChat) {
	if (!Array.isArray(chats)) {
		return;
	}
	for (let i = 0; i < chats.length; i++) {
		const chat = chats[i];
		if (!chat) {
			continue;
		}
		if (activeChat && chat.type == activeChat.type && chat.targetId == activeChat.targetId) {
			continue;
		}
		if (Array.isArray(chat.messages) && chat.messages.length > INACTIVE_KEEP_MESSAGES) {
			trimChatMessages(chat);
		} else {
			freezeChatMessages(chat);
		}
	}
}

function snapshotChatMeta(chat) {
	return {
		targetId: chat.targetId,
		type: chat.type,
		showName: chat.showName,
		headImage: chat.headImage,
		userTag: chat.userTag,
		lastContent: chat.lastContent,
		lastSendTime: chat.lastSendTime,
		unreadCount: chat.unreadCount,
		atMe: !!chat.atMe,
		atAll: !!chat.atAll,
		delete: !!chat.delete,
		isNotDisturb: !!chat.isNotDisturb,
		draftContent: chat.draftContent || '',
		pinTime: chat.pinTime || 0,
		sendNickName: chat.sendNickName || '',
		lastTimeTip: chat.lastTimeTip || 0,
		lastMsgType: chat.lastMsgType,
		stored: true
	};
}

function mergeMessages(storedMessages, incoming) {
	const list = Array.isArray(storedMessages) ? storedMessages.slice() : [];
	if (!incoming || !incoming.length) {
		return list;
	}
	const byId = new Map();
	const byTmp = new Map();
	const bySeq = new Map();
	for (let i = 0; i < list.length; i++) {
		const m = list[i];
		if (!m) {
			continue;
		}
		if (m.id) {
			byId.set(m.id, i);
		}
		if (m.tmpId) {
			byTmp.set(m.tmpId, i);
		}
		if (m.seqNo) {
			bySeq.set(m.seqNo, i);
		}
	}
	incoming.forEach((m) => {
		if (!m) {
			return;
		}
		if (m.type == MESSAGE_TYPE.TIP_TIME) {
			const exists = list.some((x) => x && x.type == MESSAGE_TYPE.TIP_TIME && x.sendTime == m.sendTime);
			if (exists) {
				return;
			}
			list.push(cloneMessage(m));
			return;
		}
		let idx = -1;
		if (m.id && byId.has(m.id)) {
			idx = byId.get(m.id);
		} else if (m.tmpId && byTmp.has(m.tmpId)) {
			idx = byTmp.get(m.tmpId);
		} else if (m.seqNo && bySeq.has(m.seqNo)) {
			idx = bySeq.get(m.seqNo);
		}
		if (idx >= 0) {
			list[idx] = Object.assign({}, list[idx], m);
			return;
		}
		const copy = cloneMessage(m);
		list.push(copy);
		if (copy.id) {
			byId.set(copy.id, list.length - 1);
		}
		if (copy.tmpId) {
			byTmp.set(copy.tmpId, list.length - 1);
		}
		if (copy.seqNo) {
			bySeq.set(copy.seqNo, list.length - 1);
		}
	});
	return list;
}

function applyPersistOps(storedMessages, ops) {
	if (!ops || !Array.isArray(storedMessages) || !storedMessages.length) {
		return storedMessages;
	}
	let list = storedMessages;
	if (ops.readedMaxId != null) {
		for (let i = 0; i < list.length; i++) {
			const m = list[i];
			if (m && m.id && m.selfSend && m.status < MESSAGE_STATUS.RECALL && m.id <= ops.readedMaxId) {
				m.status = MESSAGE_STATUS.READED;
			}
		}
	}
	if (ops.recalls && ops.recalls.length) {
		ops.recalls.forEach((recall) => {
			if (!recall) {
				return;
			}
			for (let i = 0; i < list.length; i++) {
				const m = list[i];
				if (!m) {
					continue;
				}
				if (recall.id && m.id == recall.id) {
					m.status = recall.status;
					m.content = recall.content;
					m.type = recall.type;
				}
				if (recall.quoteSrcId && m.quoteMessage && m.quoteMessage.id == recall.quoteSrcId) {
					m.quoteMessage.content = "引用内容已撤回";
					m.quoteMessage.status = MESSAGE_STATUS.RECALL;
					m.quoteMessage.type = MESSAGE_TYPE.TIP_TEXT;
				}
			}
		});
	}
	if (ops.deletes && ops.deletes.length) {
		list = list.filter((m) => {
			if (!m) {
				return false;
			}
			return !ops.deletes.some((item) => (item.id && m.id == item.id) || (item.tmpId && m.tmpId && m.tmpId == item.tmpId));
		});
	}
	return list;
}

function persistChatRecord(chatKey, chat) {
	const deleting = !!chat.delete;
	const trimmed = !!chat.messagesTrimmed;
	const meta = snapshotChatMeta(chat);
	const incoming = dropForeignPrivateMessages(chat, Array.isArray(chat.messages) ? chat.messages.map(cloneMessage) : []);
	const persistOps = {
		readedMaxId: chat._persistReadedMaxId,
		recalls: chat._persistRecalls ? chat._persistRecalls.slice() : null,
		deletes: chat._persistDeletes ? chat._persistDeletes.slice() : null
	};
	chat._persistReadedMaxId = null;
	chat._persistRecalls = null;
	chat._persistDeletes = null;
	const run = () => {
		if (deleting) {
			return localForage.removeItem(chatKey);
		}
		if (!trimmed) {
			return localForage.setItem(chatKey, Object.assign({}, meta, { messages: incoming }));
		}
		return localForage.getItem(chatKey).then((stored) => {
			if (!stored || !Array.isArray(stored.messages) || stored.messages.length === 0) {
				return localForage.setItem(chatKey, Object.assign({}, meta, { messages: incoming }));
			}
			Object.assign(stored, meta);
			stored.messages = applyPersistOps(mergeMessages(dropForeignPrivateMessages(chat, stored.messages), incoming), persistOps);
			stored.stored = true;
			return localForage.setItem(chatKey, stored);
		});
	};
	const prev = persistTasks.get(chatKey) || Promise.resolve();
	const next = prev.then(run, run);
	persistTasks.set(chatKey, next);
	next.catch(() => {}).then(() => {
		if (persistTasks.get(chatKey) === next) {
			persistTasks.delete(chatKey);
		}
	});
}

function afterChatPersist(chatKey) {
	const pending = persistTasks.get(chatKey);
	if (!pending) {
		return Promise.resolve();
	}
	return pending.then(() => {}, () => {});
}

function applyStoredMessages(chat, stored, maxCount) {
	const rawStored = stored && Array.isArray(stored.messages) ? stored.messages : [];
	const rawCurrent = Array.isArray(chat.messages) ? chat.messages : [];
	const storedList = dropForeignPrivateMessages(chat, rawStored);
	const hadStored = storedList.length > 0;
	const current = dropForeignPrivateMessages(chat, rawCurrent);
	const removedForeign = storedList !== rawStored || current !== rawCurrent;
	const merged = mergeMessages(storedList, current);
	const sliced = merged.length > maxCount ? merged.slice(-maxCount) : merged;
	chat.messages = sliced.map(cloneMessage);
	if (!hadStored && chat.messagesTrimmed) {
		chat.messagesTrimmed = true;
	} else {
		chat.messagesTrimmed = merged.length > chat.messages.length;
	}
	chat.messagesHydrated = true;
	markLoadingFail(chat);
	if (removedForeign) {
		if (chat.messages.length) {
			applyLastMessageMeta(chat);
		} else {
			chat.lastContent = '';
		}
		chat.stored = false;
	}
}

function applyLastMessageMeta(chat) {
	if (!chat || !Array.isArray(chat.messages)) {
		return;
	}
	let lastMsg = null;
	for (let i = chat.messages.length - 1; i >= 0; i--) {
		const msg = chat.messages[i];
		if (msg && msg.type !== MESSAGE_TYPE.TIP_TIME) {
			lastMsg = msg;
			break;
		}
	}
	if (!lastMsg) {
		return;
	}
	chat.lastMsgType = lastMsg.type;
	if (lastMsg.type == MESSAGE_TYPE.IMAGE) {
		chat.lastContent = "[图片]";
	} else if (lastMsg.type == MESSAGE_TYPE.VIDEO) {
		chat.lastContent = "[视频]";
	} else if (lastMsg.type == MESSAGE_TYPE.FILE) {
		chat.lastContent = "[文件]";
	} else if (lastMsg.type == MESSAGE_TYPE.USER_CARD) {
		try {
			chat.lastContent = "[个人名片] " + JSON.parse(lastMsg.content).nickName;
		} catch (e) {
			chat.lastContent = "[个人名片]";
		}
	} else if (lastMsg.type == MESSAGE_TYPE.GROUP_CARD) {
		try {
			chat.lastContent = "[群名片] " + JSON.parse(lastMsg.content).groupName;
		} catch (e) {
			chat.lastContent = "[群名片]";
		}
	} else if (lastMsg.type == MESSAGE_TYPE.AUDIO) {
		chat.lastContent = "[语音]";
	} else if (lastMsg.type == MESSAGE_TYPE.ACT_RT_VOICE) {
		chat.lastContent = "[语音通话]";
	} else if (lastMsg.type == MESSAGE_TYPE.ACT_RT_VIDEO) {
		chat.lastContent = "[视频通话]";
	} else if (lastMsg.type == MESSAGE_TYPE.SYSTEM_MESSAGE) {
		chat.lastContent = lastMsg.title;
	} else if (lastMsg.type == MESSAGE_TYPE.TEXT ||
		lastMsg.type == MESSAGE_TYPE.RECALL ||
		lastMsg.type == MESSAGE_TYPE.TIP_TEXT) {
		chat.lastContent = lastMsg.content;
	}
	if (lastMsg.sendNickName) {
		chat.sendNickName = lastMsg.sendNickName;
	} else {
		chat.sendNickName = '';
	}
	if (lastMsg.sendTime) {
		chat.lastSendTime = lastMsg.sendTime;
	}
}

function loadChatsInBatches(keys) {
	const chats = [];
	let i = 0;
	const next = () => {
		if (i >= keys.length) {
			return Promise.resolve(chats);
		}
		const batch = keys.slice(i, i + LOAD_BATCH);
		i += LOAD_BATCH;
		return Promise.all(batch.map((key) => localForage.getItem(key))).then((items) => {
			for (let j = 0; j < items.length; j++) {
				const chat = items[j];
				if (!chat) {
					continue;
				}
				if (!Array.isArray(chat.messages)) {
					chat.messages = [];
				}
				trimChatMessages(chat, INACTIVE_KEEP_MESSAGES);
				chats.push(chat);
			}
			return next();
		});
	};
	return next();
}

function reSortPinedChats(pinedChats, chats) {
	// 边界条件检查
	if (!Array.isArray(chats)) {
		return chats || [];
	}
	if (!Array.isArray(pinedChats) || pinedChats.length === 0) {
		// 没有置顶项时，只按最后发送时间排序
		chats.sort((chat1, chat2) => {
			return (chat2.lastSendTime || 0) - (chat1.lastSendTime || 0);
		});
		return chats;
	}

	const list = ['', 'PRIVATE', 'GROUP'];
	// 先按最后发送时间排序
	chats.sort((chat1, chat2) => {
		return (chat2.lastSendTime || 0) - (chat1.lastSendTime || 0);
	});

	// 设置 pinTime：应该用 forEach 而不是 map（因为我们是在修改元素而不是创建新数组）
	chats.forEach((chat) => {
		// 该字段很关键，是排序的依据
		chat.pinTime = 0;
		// 查找匹配的置顶项（只匹配第一个，避免重复覆盖）
		for (const pinedChat of pinedChats) {
			if (chat.type === list[pinedChat.pinType] && String(chat.targetId) === String(pinedChat.pinId)) {
				chat.pinTime = pinedChat.pinTime || 0;
				break; // 找到第一个匹配的就退出，避免重复匹配
			}
		}
	});

	// 根据 pinTime 排序（保持原有逻辑）
	// 注意：如果都是置顶的，按 pinTime 降序（最近置顶的在前）
	// 如果都是未置顶的（pinTime = 0），保持按 lastSendTime 的顺序（使用稳定的排序）
	chats.sort((chat1, chat2) => {
		const pinTime1 = chat1.pinTime || 0;
		const pinTime2 = chat2.pinTime || 0;
		// 按 pinTime 降序排序：置顶的（pinTime > 0）会排在未置顶的（pinTime = 0）前面
		const diff = pinTime2 - pinTime1;
		if (diff !== 0) {
			return diff;
		}
		// 如果 pinTime 相同（都是 0 或相同的时间戳），按 lastSendTime 排序以保持稳定性
		const time1 = chat1.lastSendTime || 0;
		const time2 = chat2.lastSendTime || 0;
		return time2 - time1;
	});

	return chats;
}

export default {
	state: {
		activeChat: null,
		privateMsgMaxId: 0,
		groupMsgMaxId: 0,
		systemMsgMaxSeqNo: 0,
		loading: false,
		chats: [],
		pinedChats: [],
		localDataCorrupted: false,
		needFullResync: false,
		isReconnecting: false, // 是否在重连中，重连时保持现有会话可见
		hiddenPrivateIds: [],
	},

	mutations: {
		setLocalDataCorrupted(state, flag) {
			state.localDataCorrupted = !!flag;
		},
		setNeedFullResync(state, flag) {
			state.needFullResync = !!flag;
		},
		setIsReconnecting(state, flag) {
			state.isReconnecting = !!flag;
		},
		setPinedChats(state, list) {
			state.pinedChats = list
		},
		initChats(state, chatsData) {
			state.chats = [];
			state.privateMsgMaxId = chatsData.privateMsgMaxId || 0;
			state.groupMsgMaxId = chatsData.groupMsgMaxId || 0;
			state.systemMsgMaxSeqNo = chatsData.systemMsgMaxSeqNo || 0;
			if (Array.isArray(chatsData.hiddenPrivateIds)) {
				state.hiddenPrivateIds = chatsData.hiddenPrivateIds.slice();
			}
			cacheChats = (chatsData.chats || []).filter((chat) => {
				if (!chat || chat.delete) {
					return false;
				}
				if (chat.type == 'PRIVATE' && isHiddenPrivateId(state, chat.targetId)) {
					return false;
				}
				return true;
			});
			cacheChats.forEach((chat) => {
				if (!chat) {
					return;
				}
				if (!Array.isArray(chat.messages)) {
					chat.messages = [];
					return;
				}
				const cleaned = dropForeignPrivateMessages(chat, chat.messages);
				if (cleaned !== chat.messages) {
					chat.messages = cleaned;
					if (cleaned.length) {
						applyLastMessageMeta(chat);
					} else {
						chat.lastContent = '';
					}
					chat.stored = false;
				}
				markLoadingFail(chat);
			})
			rebuildChatIndex(cacheChats);
		},
		openChat(state, chatInfo) {
			if (chatInfo && chatInfo.type == 'PRIVATE' && shouldSkipPrivateChat(this, state, chatInfo.targetId, !!chatInfo.activeChat)) {
				return;
			}
			if (chatInfo && chatInfo.type == 'PRIVATE' && chatInfo.activeChat && this.getters.isFriend(chatInfo.targetId)) {
				this.commit("unhidePrivateChat", chatInfo.targetId);
			}
			let chats = this.getters.findChats()
			let chat = this.getters.findChat(chatInfo);
			if (chat) {
				const idx = chats.indexOf(chat);
				if (idx >= 0) {
					this.commit("insertNew", idx)
				}
			}
			if (chat != null && chatInfo.userTag !== undefined && chat.userTag !== chatInfo.userTag) {
				chat.userTag = chatInfo.userTag;
				chat.stored = false;
			}
			// 创建会话
			if (chat == null) {
				chat = {
					targetId: chatInfo.targetId,
					type: chatInfo.type,
					showName: chatInfo.showName,
					headImage: chatInfo.headImage,
					userTag: chatInfo.userTag,
					lastContent: "",
					lastSendTime: new Date().getTime(),
					unreadCount: 0,
					messages: [],
					atMe: false,
					atAll: false,
					stored: false,
					delete: false,
					isNotDisturb: false,
					draftContent: '',
					pinTime: 0,
					messagesTrimmed: false,
					messagesHydrated: true,
				};
				chats.unshift(chat);
				registerChat(chat);
				chats = reSortPinedChats(state.pinedChats, chats);
			}
			// 需要打开会话则执行
			if (chatInfo.activeChat) {
				const prev = state.activeChat;
				ensureMutableMessages(chat);
				state.activeChat = chat;
				if (prev && prev !== chat && !this.getters.isLoading()) {
					trimChatMessages(prev);
				}
			}

		},
		activeChat(state, idx) {
			const prev = state.activeChat;
			try {
				if (idx < 0) {
					state.activeChat = null;
				} else {
					let chats = this.getters.findChats();
					const chat = chats[idx];
					if (chat) {
						ensureMutableMessages(chat);
					}
					state.activeChat = chat;
				}
			} catch (e) {
				state.activeChat = null;
			}
			if (prev && prev !== state.activeChat && !this.getters.isLoading()) {
				trimChatMessages(prev);
			}
		},
		resetUnreadCount(state, chatInfo) {
			let chat = this.getters.findChat(chatInfo);
			if (!chat) {
				return;
			}
			chat.unreadCount = 0;
			chat.atMe = false;
			chat.atAll = false;
			chat.stored = false;
			this.commit("saveToStorage");
		},
		readedMessage(state, pos) {
			let chat = this.getters.findChatByFriend(pos.friendId);
			if (!chat) return;
			ensureMutableMessages(chat);
			chat.messages.forEach((m) => {
				if (m.id && m.selfSend && m.status < MESSAGE_STATUS.RECALL) {
					if (!pos.maxId || m.id <= pos.maxId) {
						m.status = MESSAGE_STATUS.READED
						chat.stored = false;
					}
				}
			})
			chat._persistReadedMaxId = !pos.maxId ? Number.MAX_SAFE_INTEGER : pos.maxId;
			chat.stored = false;
			this.commit("saveToStorage");
		},
		cleanMessage(state, idx) {
			if (this.getters.isLoading()) {
				return;
			}
			let chats = this.getters.findChats();
			let chat = chats[idx];
			if (!chat) {
				return;
			}
			ensureMutableMessages(chat);
			chat.lastContent = '';
			chat.unreadCount = 0;
			chat.atMe = false;
			chat.atAll = false;
			chat.stored = false
			chat.messages = [];
			chat.messagesTrimmed = false;
			chat.messagesHydrated = true;
			chat._persistReadedMaxId = null;
			chat._persistRecalls = null;
			chat._persistDeletes = null;
			this.commit("saveToStorage");
		},
		saveDraftContent(state, payload) {
			if (this.getters.isLoading()) {
				return;
			}
			let content = payload;
			let chatInfo = state.activeChat;
			if (payload && typeof payload === 'object' && !Array.isArray(payload) &&
				(Object.prototype.hasOwnProperty.call(payload, 'content') || Object.prototype.hasOwnProperty.call(payload, 'chat'))) {
				content = payload.content;
				chatInfo = payload.chat || state.activeChat;
			}
			let chat = this.getters.findChat(chatInfo);
			if (!chat) {
				return;
			}
			chat.draftContent = content;
			chat.stored = false
			this.commit("saveToStorage");
		},
		removeChat(state, idx) {
			let chats = this.getters.findChats();
			if (chats[idx] == state.activeChat) {
				state.activeChat = null;
			}
			chats[idx].delete = true;
			chats[idx].stored = false;
			this.commit("saveToStorage");
		},
		removeAllChat(state) {
			let chats = this.getters.findChats();
			chats.forEach((chat) => {
				chat.delete = true;
				chat.stored = false;
			})
			this.commit("saveToStorage");
			setTimeout(() => {
				let userId = userStore.state.userInfo.id;
				let key = "chats-" + userId;
				localForage.removeItem(key);
				window.location.reload();
			}, 1000);
		},
		hidePrivateChat(state, friendId) {
			const key = normalizeHiddenId(friendId);
			if (!key || isHiddenPrivateId(state, key)) {
				return;
			}
			state.hiddenPrivateIds = (state.hiddenPrivateIds || []).concat([friendId]);
		},
		unhidePrivateChat(state, friendId) {
			const key = normalizeHiddenId(friendId);
			if (!key || !isHiddenPrivateId(state, key)) {
				return;
			}
			state.hiddenPrivateIds = (state.hiddenPrivateIds || []).filter((id) => String(id) !== key);
		},
		removePrivateChat(state, friendId) {
			this.commit("hidePrivateChat", friendId);
			let chat = this.getters.findChatByFriend(friendId);
			if (!chat) {
				this.commit("saveToStorage");
				return;
			}
			const chats = this.getters.findChats();
			const idx = chats.indexOf(chat);
			if (idx >= 0) {
				this.commit("removeChat", idx)
			} else {
				this.commit("saveToStorage");
			}
		},
		setLoading(state, loading) {
			state.loading = loading;
		},
		removeGroupChat(state, groupId) {
			let chat = this.getters.findChatByGroup(groupId);
			if (!chat) {
				return;
			}
			const chats = this.getters.findChats();
			const idx = chats.indexOf(chat);
			if (idx >= 0) {
				this.commit("removeChat", idx)
			}
		},
		moveTop(state, idx) {
			// 加载中不移动，很耗性能
			if (this.getters.isLoading()) {
				return;
			}
			let chats = this.getters.findChats();
			let chat = chats[idx];
			chat.pinTime = new Date().getTime();
			chat.stored = false;
			chats = reSortPinedChats(state.pinedChats, chats);
			this.commit("saveToStorage");
		},
		chatToTop(state, idx) {
			// 加载中不移动，很耗性能
			if (this.getters.isLoading()) {
				return;
			}
			let chats = this.getters.findChats();
			let chat = chats[idx];
			chat.lastSendTime = new Date().getTime();
			chat.stored = false;
			chats = reSortPinedChats(state.pinedChats, chats);
			this.commit("saveToStorage");
		},
		cancelTop(state, idx) {
			// 加载中不移动，很耗性能
			if (this.getters.isLoading()) {
				return;
			}
			let chats = this.getters.findChats();
			let chat = chats[idx];
			chat.pinTime = 0;
			chat.stored = false;
			chats = reSortPinedChats(state.pinedChats, chats);
			this.commit("saveToStorage");
		},
		noHint(state, idx) {
			let chats = this.getters.findChats();
			let chat = chats[idx];
			chat.isNotDisturb = true;
			chat.stored = false;
			this.commit("saveToStorage");
		},
		cancelNoHint(state, idx) {
			let chats = this.getters.findChats();
			let chat = chats[idx];
			chat.isNotDisturb = false;
			chat.stored = false;
			this.commit("saveToStorage");
		},
		insertNew(state, idx) {
			// 加载中不移动，很耗性能
			if (this.getters.isLoading()) {
				return;
			}
			let chats = this.getters.findChats();
			let chat = chats[idx];
			chat.lastSendTime = new Date().getTime();
			chat.stored = false;
			chats = reSortPinedChats(state.pinedChats, chats);
			this.commit("saveToStorage");
		},
		insertMessage(state, [msgInfo, chatInfo]) {
			const chatDetail = chatInfo || state.activeChat
			let type = chatDetail.type;
			// 获取聊天会话（必须在检查消息是否存在之前获取，确保使用最新的 chat 对象）
			let chat = this.getters.findChat(chatDetail);
			if (!chat) {
				console.warn('Chat not found in insertMessage:', chatDetail);
				return;
			}
			if (!privateMessageBelongs(chat, msgInfo)) {
				console.warn('Skip message that does not belong to chat', chat.type, chat.targetId, msgInfo && msgInfo.id);
				return;
			}
			ensureMutableMessages(chat);
			// 如果是已存在消息：批量同步时保留本地不覆盖；非批量时覆盖并落库
			let message = this.getters.findMessage(chat, msgInfo);
				if (message) {
					Object.assign(message, msgInfo);
					chat.stored = false;
					this.commit("saveToStorage");
					return;
				}

			// 记录消息的最大id（只有在消息真正要插入时才更新，确保 maxId 的准确性）
			if (msgInfo.id && type == "PRIVATE" && msgInfo.id > state.privateMsgMaxId && !msgInfo.selfSend) {
				state.privateMsgMaxId = msgInfo.id;
			}
			if (msgInfo.id && type == "GROUP" && msgInfo.id > state.groupMsgMaxId && !msgInfo.selfSend) {
				state.groupMsgMaxId = msgInfo.id;
			}
			if (msgInfo.seqNo && type == "SYSTEM" && msgInfo.seqNo > state.systemMsgMaxSeqNo && !msgInfo.selfSend) {
				state.systemMsgMaxSeqNo = msgInfo.seqNo;
			}
			// 先插入消息，再根据最后一条实际消息更新 lastContent 和 lastSendTime
			// 未读加1
			if (!msgInfo.selfSend && msgInfo.status != MESSAGE_STATUS.READED &&
				msgInfo.status != MESSAGE_STATUS.RECALL && msgInfo.type != MESSAGE_TYPE.TIP_TEXT) {
				chat.unreadCount++;
			}
			// 是否有人@我
			if (!msgInfo.selfSend && chat.type == "GROUP" && msgInfo.atUserIds &&
				msgInfo.status != MESSAGE_STATUS.READED) {
				let userId = userStore.state.userInfo.id;
				if (msgInfo.atUserIds.indexOf(userId) >= 0) {
					chat.atMe = true;
				}
				if (msgInfo.atUserIds.indexOf(-1) >= 0) {
					chat.atAll = true;
				}
			}
			// 根据id顺序插入，防止消息乱序；从后往前扫，避免扫整段历史
			let insertPos = chat.messages.length;
			if (type != "SYSTEM" && msgInfo.id && msgInfo.id > 0) {
				for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
					const msg = chat.messages[idx];
					if (msg && msg.id && msg.id > 0) {
						if (msgInfo.id < msg.id) {
							insertPos = idx;
						} else {
							break;
						}
					}
				}
			}
			if (type == "SYSTEM" && msgInfo.seqNo && msgInfo.seqNo > 0) {
				for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
					const msg = chat.messages[idx];
					if (msg && msg.seqNo && msg.seqNo > 0) {
						if (msgInfo.seqNo < msg.seqNo) {
							insertPos = idx;
						} else {
							break;
						}
					}
				}
			}
			// 间隔大于10分钟插入时间显示（将 TIP_TIME 插入到与消息相同的位置，避免错位）
			let tipInserted = false;
			if (!chat.lastTimeTip || (chat.lastTimeTip < msgInfo.sendTime - 600 * 1000)) {
				chat.messages.splice(insertPos, 0, {
					sendTime: msgInfo.sendTime,
					type: MESSAGE_TYPE.TIP_TIME,
				});
				tipInserted = true;
				if (!chat.lastTimeTip || chat.lastTimeTip < msgInfo.sendTime) {
					chat.lastTimeTip = msgInfo.sendTime;
				}
			}
			if (tipInserted) {
				insertPos += 1;
			}
			chat.messages.splice(insertPos, 0, msgInfo);
			applyLastMessageMeta(chat);
			chat.stored = false;
			this.commit("saveToStorage");
			if (!this.getters.isLoading() && state.activeChat !== chat &&
				Array.isArray(chat.messages) && chat.messages.length > INACTIVE_TRIM_AT) {
				trimChatMessages(chat);
			}
		},
		updateMessage(state, [msgInfo, chatInfo]) {
			let chat = this.getters.findChat(chatInfo);
			if (!chat) {
				return;
			}
			if (!privateMessageBelongs(chat, msgInfo)) {
				return;
			}
			ensureMutableMessages(chat);
			let message = this.getters.findMessage(chat, msgInfo);
			if (message) {
				Object.assign(message, msgInfo);
				chat.stored = false;
				this.commit("saveToStorage");
			}
		},
		deleteMessage(state, [msgInfo, chatInfo]) {
			let chat = this.getters.findChat(chatInfo);
			if (!chat) {
				return;
			}
			ensureMutableMessages(chat);
			for (let idx = 0; idx < chat.messages.length; idx++) {
				if (chat.messages[idx].id && chat.messages[idx].id == msgInfo.id) {
					chat.messages.splice(idx, 1);
					break;
				}
				if (chat.messages[idx].tmpId && chat.messages[idx].tmpId == msgInfo.tmpId) {
					chat.messages.splice(idx, 1);
					break;
				}
			}
			if (!chat._persistDeletes) {
				chat._persistDeletes = [];
			}
			chat._persistDeletes.push({
				id: msgInfo.id,
				tmpId: msgInfo.tmpId
			});
			chat.stored = false;
			this.commit("saveToStorage");
		},
		recallMessage(state, [msgInfo, chatInfo]) {
			let chat = this.getters.findChat(chatInfo);
			if (!chat) return;
			ensureMutableMessages(chat);
			let id = msgInfo.content;
			let name = msgInfo.selfSend ? '你' : chat.type == 'PRIVATE' ? '对方' : msgInfo.sendNickName;
			const recallPatch = {
				id: id,
				content: name + "撤回了一条消息",
				type: MESSAGE_TYPE.TIP_TEXT,
				status: MESSAGE_STATUS.RECALL,
				quoteSrcId: msgInfo.id
			};
			if (!chat._persistRecalls) {
				chat._persistRecalls = [];
			}
			chat._persistRecalls.push(recallPatch);
			for (let idx = 0; idx < chat.messages.length; idx++) {
				let m = chat.messages[idx];
				if (m.id && m.id == id) {
					m.status = MESSAGE_STATUS.RECALL;
					m.content = name + "撤回了一条消息";
					m.type = MESSAGE_TYPE.TIP_TEXT
					const list = chat.messages.filter(msg => msg.type !== MESSAGE_TYPE.TIP_TIME);
					const lastMsg = list[list.length - 1];
					if (lastMsg && lastMsg.id === m.id) {
						chat.lastContent = m.content;
						chat.lastSendTime = m.sendTime;
						chat.sendNickName = '';
						chat.lastMsgType = m.type;
					}
				}
				if (m.quoteMessage && m.quoteMessage.id == msgInfo.id) {
					m.quoteMessage.content = "引用内容已撤回";
					m.quoteMessage.status = MESSAGE_STATUS.RECALL;
					m.quoteMessage.type = MESSAGE_TYPE.TIP_TEXT
				}
			}
			chat.stored = false;
			this.commit("saveToStorage");
		},
		updateChatFromFriend(state, friend) {
			let chat = this.getters.findChatByFriend(friend.id);
			if (chat && (chat.headImage != friend.headImage ||
				chat.showName != friend.showNickName ||
				chat.userTag != friend.userTag)) {
				chat.headImage = friend.headImage;
				chat.showName = friend.showNickName;
				chat.userTag = friend.userTag;
				chat.stored = false;
				this.commit("saveToStorage")
			}
		},
		updateChatFromUser(state, user) {
			let chat = this.getters.findChatByFriend(user.id);
			const nextHeadImage = user.headImage || user.headImageThumb;
			if (chat && (chat.headImage != nextHeadImage ||
				chat.showName != user.nickName ||
				chat.userTag != user.userTag)) {
				chat.headImage = nextHeadImage;
				chat.showName = user.nickName;
				chat.userTag = user.userTag;
				chat.stored = false;
				this.commit("saveToStorage")
			}
		},
		syncChatUserTagFromFriends(state) {
			const chats = this.getters.findChats();
			if (!Array.isArray(chats) || !chats.length) {
				return;
			}
			const findFriend = this.getters.findFriend;
			if (!findFriend) {
				return;
			}
			let changed = false;
			chats.forEach((chat) => {
				if (!chat || chat.type !== 'PRIVATE') {
					return;
				}
				const friend = findFriend(chat.targetId);
				if (friend && chat.userTag !== friend.userTag) {
					chat.userTag = friend.userTag;
					chat.stored = false;
					changed = true;
				}
			});
			if (changed) {
				this.commit("saveToStorage");
			}
		},
		updateChatFromGroup(state, group) {
			let chat = this.getters.findChatByGroup(group.id);
			if (!chat) {
				return;
			}
			let changed = false;
			if (chat.headImage != group.headImageThumb ||
				chat.showName != group.showGroupName) {
				chat.headImage = group.headImageThumb;
				chat.showName = group.showGroupName;
				changed = true;
			}
			if (typeof group.isNotDisturb === 'boolean' && chat.isNotDisturb !== group.isNotDisturb) {
				chat.isNotDisturb = group.isNotDisturb;
				changed = true;
			}
			if (changed) {
				chat.stored = false;
				this.commit("saveToStorage");
			}
		},
		refreshChats(state) {
			if (!cacheChats) {
				return;
			}
			const chats = reSortPinedChats(state.pinedChats, cacheChats);
			const active = state.activeChat;
			chats.forEach((chat) => {
				if (!chat) {
					return;
				}
				if (active && chat.type == active.type && chat.targetId == active.targetId) {
					ensureMutableMessages(chat);
					return;
				}
				freezeChatMessages(chat);
			});
			state.chats = chats.filter((chat) => {
				if (!chat || chat.delete) {
					return false;
				}
				if (chat.type == 'PRIVATE' && (isHiddenPrivateId(state, chat.targetId) || !this.getters.isFriend(chat.targetId))) {
					return false;
				}
				return true;
			});
			if (state.activeChat && state.chats.indexOf(state.activeChat) < 0) {
				state.activeChat = null;
			}
			cacheChats = null;
			rebuildChatIndex(state.chats);
			this.commit("syncChatUserTagFromFriends");
			this.commit("saveToStorage");
			this.commit("trimInactiveChatMessages");
		},
		trimInactiveChatMessages(state) {
			trimInactiveChats(this.getters.findChats(), state.activeChat);
		},
		prepareActiveChatMessages(state, chat) {
			ensureMutableMessages(chat);
			markLoadingFail(chat);
		},
		applyHydratedMessages(state, payload) {
			if (!payload || !payload.chat) {
				return;
			}
			applyStoredMessages(payload.chat, payload.stored, payload.maxCount || MAX_ACTIVE_MESSAGES);
		},
		prependOlderMessages(state, payload) {
			const chat = payload && payload.chat;
			const stored = payload && payload.stored;
			const take = (payload && payload.take) || 200;
			if (!chat || !stored || !Array.isArray(stored.messages) || !stored.messages.length) {
				return;
			}
			ensureMutableMessages(chat);
			const current = chat.messages || [];
			const storedMsgs = dropForeignPrivateMessages(chat, stored.messages);
			const room = Math.max(0, MAX_MEMORY_MESSAGES - current.length);
			const actualTake = Math.min(take, room);
			if (actualTake <= 0) {
				return;
			}
			let end = storedMsgs.length;
			const first = current.find((m) => m && (m.id || m.seqNo));
			if (first) {
				const idx = storedMsgs.findIndex((m) => (first.id && m.id == first.id) || (first.seqNo && m.seqNo == first.seqNo));
				if (idx === 0) {
					chat.messagesTrimmed = false;
					return;
				}
				if (idx > 0) {
					end = idx;
				} else {
					const merged = mergeMessages(storedMsgs, current);
					const extra = Math.min(actualTake, Math.max(0, merged.length - current.length));
					if (extra <= 0) {
						return;
					}
					chat.messages = merged.slice(merged.length - current.length - extra).map(cloneMessage);
					chat.messagesTrimmed = merged.length > chat.messages.length;
					chat.messagesHydrated = true;
					return;
				}
			}
			const from = Math.max(0, end - actualTake);
			if (from >= end) {
				return;
			}
			const older = storedMsgs.slice(from, end).map(cloneMessage);
			chat.messages = older.concat(current);
			chat.messagesTrimmed = from > 0;
			chat.messagesHydrated = true;
		},
		saveToStorage(state) {
			if (this.getters.isLoading()) {
				return;
			}
			let userId = userStore.state.userInfo.id;
			if (!userId) {
				return;
			}
			let key = "chats-" + userId;
			let chatKeys = [];
			state.chats.forEach((chat) => {
				let chatKey = `${key}-${chat.type}-${chat.targetId}`
				if (!chat.stored) {
					if (chat.delete) {
						unregisterChat(chat);
					}
					persistChatRecord(chatKey, chat);
					chat.stored = true;
				}
				if (!chat.delete) {
					chatKeys.push(chatKey);
				}
			})
			let chatsData = {
				privateMsgMaxId: state.privateMsgMaxId,
				groupMsgMaxId: state.groupMsgMaxId,
				systemMsgMaxSeqNo: state.systemMsgMaxSeqNo,
				chatKeys: chatKeys,
				hiddenPrivateIds: state.hiddenPrivateIds || []
			}
			localForage.setItem(key, chatsData)
			if (state.chats.some(chat => chat.delete)) {
				state.chats = state.chats.filter(chat => !chat.delete)
				rebuildChatIndex(state.chats);
			}
		},
		clear(state) {
			cacheChats = []
			rebuildChatIndex([]);
			state.chats = [];
			state.activeChat = null;
			state.hiddenPrivateIds = [];
		},
		resetMaxIds(state) {
			state.privateMsgMaxId = 0;
			state.groupMsgMaxId = 0;
			state.systemMsgMaxSeqNo = 0;
		}
	},
	actions: {
		hydrateChatMessages(context, payload) {
			const chat = payload && payload.chat ? payload.chat : payload;
			const maxCount = (payload && payload.maxCount) || MAX_ACTIVE_MESSAGES;
			if (!chat) {
				return Promise.resolve();
			}
			if (chat.messagesHydrated && !chat.messagesTrimmed) {
				context.commit("prepareActiveChatMessages", chat);
				return Promise.resolve();
			}
			if (chat.messagesHydrated && Array.isArray(chat.messages) && chat.messages.length >= maxCount) {
				context.commit("prepareActiveChatMessages", chat);
				return Promise.resolve();
			}
			const userId = userStore.state.userInfo.id;
			if (!userId) {
				context.commit("prepareActiveChatMessages", chat);
				return Promise.resolve();
			}
			const key = storageChatKey(userId, chat);
			return afterChatPersist(key).then(() => localForage.getItem(key)).then((stored) => {
				context.commit("applyHydratedMessages", {
					chat,
					stored,
					maxCount
				});
			}).catch(() => {
				context.commit("prepareActiveChatMessages", chat);
			});
		},
		activateChat(context, idx) {
			const seq = ++activateSeq;
			if (idx < 0) {
				context.commit("activeChat", -1);
				return Promise.resolve();
			}
			const chats = context.getters.findChats();
			const chat = chats[idx];
			return context.dispatch("hydrateChatMessages", chat).then(() => {
				if (seq !== activateSeq) {
					if (chat && context.state.activeChat !== chat) {
						context.commit("trimInactiveChatMessages");
					}
					return;
				}
				const chatsNow = context.getters.findChats();
				const idxNow = chat ? chatsNow.indexOf(chat) : -1;
				context.commit("activeChat", idxNow);
			});
		},
		loadOlderChatMessages(context, chat) {
			if (!chat || !chat.messagesTrimmed) {
				return Promise.resolve(0);
			}
			if (Array.isArray(chat.messages) && chat.messages.length >= MAX_MEMORY_MESSAGES) {
				return Promise.resolve(0);
			}
			const userId = userStore.state.userInfo.id;
			if (!userId) {
				return Promise.resolve(0);
			}
			const before = Array.isArray(chat.messages) ? chat.messages.length : 0;
			const key = storageChatKey(userId, chat);
			return afterChatPersist(key).then(() => localForage.getItem(key)).then((stored) => {
				context.commit("prependOlderMessages", {
					chat,
					stored,
					take: 200
				});
				const after = Array.isArray(chat.messages) ? chat.messages.length : 0;
				return Math.max(0, after - before);
			}).catch(() => 0);
		},
		peekChatHistoryMessages(context, payload) {
			const chat = payload && payload.chat ? payload.chat : payload;
			const maxCount = (payload && payload.maxCount) || 8000;
			if (!chat) {
				return Promise.resolve([]);
			}
			const current = Array.isArray(chat.messages) ? chat.messages : [];
			if (!chat.messagesTrimmed && current.length) {
				const sliced = current.length > maxCount ? current.slice(-maxCount) : current;
				return Promise.resolve(sliced.map(cloneMessage));
			}
			const userId = userStore.state.userInfo.id;
			if (!userId) {
				return Promise.resolve(current.map(cloneMessage));
			}
			const key = storageChatKey(userId, chat);
			return afterChatPersist(key).then(() => localForage.getItem(key)).then((stored) => {
				const storedList = stored && Array.isArray(stored.messages) ? stored.messages : [];
				const merged = mergeMessages(storedList, current);
				const sliced = merged.length > maxCount ? merged.slice(-maxCount) : merged;
				return sliced.map(cloneMessage);
			}).catch(() => current.map(cloneMessage));
		},
		findChatsMatchingKeyword(context, payload) {
			const keyword = ((payload && payload.keyword) || payload || '').toString().trim().toLowerCase();
			const isCancelled = payload && payload.isCancelled;
			if (!keyword) {
				return Promise.resolve([]);
			}
			const chats = (context.getters.findChats() || []).filter((chat) => chat && !chat.delete);
			const userId = userStore.state.userInfo.id;
			const hits = [];
			const pending = [];
			for (let i = 0; i < chats.length; i++) {
				const chat = chats[i];
				if (chatMatchesKeyword(chat, keyword)) {
					hits.push(chatSearchKey(chat));
					continue;
				}
				if (!userId) {
					continue;
				}
				if (chat.messagesHydrated && !chat.messagesTrimmed) {
					continue;
				}
				pending.push(chat);
			}
			const scan = (start) => {
				if (isCancelled && isCancelled()) {
					return Promise.resolve(hits);
				}
				if (start >= pending.length) {
					return Promise.resolve(hits);
				}
				const batch = pending.slice(start, start + LOAD_BATCH);
				return Promise.all(batch.map((chat) => {
					const key = storageChatKey(userId, chat);
					return afterChatPersist(key).then(() => localForage.getItem(key)).then((stored) => {
						const list = stored && Array.isArray(stored.messages) ? stored.messages : [];
						if (messagesMatchKeyword(list, keyword)) {
							hits.push(chatSearchKey(chat));
						}
					}).catch(() => {});
				})).then(() => new Promise((resolve) => {
					setTimeout(() => resolve(scan(start + LOAD_BATCH)), 0);
				}));
			};
			return scan(0);
		},
		validateLocalData(context) {
			try {
				const chats = context.getters.findChats();
				if (!Array.isArray(chats)) {
					context.commit("setLocalDataCorrupted", true);
					return Promise.resolve();
				}
				if (chats.length > 200) {
					context.commit("setLocalDataCorrupted", false);
					return Promise.resolve();
				}
				let corrupted = false;
				chats.forEach((chat) => {
					if (!chat || !Array.isArray(chat.messages)) {
						corrupted = true;
						return;
					}
					if (chat.messagesTrimmed) {
						return;
					}
					let lastComparable = -1;
					let lastTime = 0;
					chat.messages.forEach((m) => {
						if (!m) {
							corrupted = true;
							return;
						}
						if (m.sendTime && typeof m.sendTime === 'number') {
							if (lastTime > 0 && m.sendTime < lastTime) {
								corrupted = true;
							}
							lastTime = Math.max(lastTime, m.sendTime);
						}
						if (chat.type == 'SYSTEM') {
							if (m.seqNo && m.seqNo > 0) {
								if (lastComparable > 0 && m.seqNo < lastComparable) {
									corrupted = true;
								}
								lastComparable = m.seqNo;
							}
						} else {
							if (m.id && m.id > 0) {
								if (lastComparable > 0 && m.id < lastComparable) {
									corrupted = true;
								}
								lastComparable = m.id;
							}
						}
					});
					if (chat.lastSendTime && typeof chat.lastSendTime === 'number' && lastTime > 0 && chat.lastSendTime < lastTime) {
						corrupted = true;
					}
				});
				context.commit("setLocalDataCorrupted", corrupted);
			} catch (e) {
				console.warn('validateLocalData error:', e);
				context.commit("setLocalDataCorrupted", true);
			}
			return Promise.resolve();
		},
		forceResyncChats(context) {
			return new Promise((resolve) => {
				try {
					let userId = userStore.state.userInfo.id;
					if (!userId) {
						context.commit("setLocalDataCorrupted", false);
						context.commit("setNeedFullResync", true);
						context.commit("clear");
						context.commit("resetMaxIds");
						resolve();
						return;
					}
					let key = "chats-" + userId;
					localForage.getItem(key).then((chatsData) => {
						const keys = (chatsData && chatsData.chatKeys) ? chatsData.chatKeys : [];
						const removes = keys.map(k => localForage.removeItem(k));
						Promise.all(removes).then(() => {
							localForage.removeItem(key).then(() => {
								context.commit("setLocalDataCorrupted", false);
								context.commit("setNeedFullResync", true);
								context.commit("clear");
								context.commit("resetMaxIds");
								resolve();
							}).catch(() => {
								context.commit("setLocalDataCorrupted", false);
								context.commit("setNeedFullResync", true);
								context.commit("clear");
								context.commit("resetMaxIds");
								resolve();
							});
						}).catch(() => {
							localForage.removeItem(key).then(() => {
								context.commit("setLocalDataCorrupted", false);
								context.commit("setNeedFullResync", true);
								context.commit("clear");
								context.commit("resetMaxIds");
								resolve();
							}).catch(() => {
								context.commit("setLocalDataCorrupted", false);
								context.commit("setNeedFullResync", true);
								context.commit("clear");
								context.commit("resetMaxIds");
								resolve();
							});
						});
					}).catch(() => {
						context.commit("setLocalDataCorrupted", false);
						context.commit("setNeedFullResync", true);
						context.commit("clear");
						context.commit("resetMaxIds");
						resolve();
					});
				} catch (e) {
					console.warn('forceResyncChats error:', e);
					context.commit("setLocalDataCorrupted", false);
					context.commit("setNeedFullResync", true);
					context.commit("clear");
					context.commit("resetMaxIds");
					resolve();
				}
			});
		},
		loadPinedChats(context) {
			return new Promise((resolve, reject) => {
				http({
					url: '/user/chat/findPinChatList',
					method: 'GET'
				}).then((list) => {
					context.commit("setPinedChats", list);
					resolve();
				}).catch((res) => {
					reject(res);
				});
			})
		},
		loadChat(context) {
			return new Promise((resolve, reject) => {
				let userId = userStore.state.userInfo.id;
				let key = "chats-" + userId;
				localForage.getItem(key).then((chatsData) => {
					if (!chatsData) {
						resolve();
					} else if (chatsData.chats) {
						const chats = chatsData.chats || [];
						chats.forEach((chat) => {
							if (chat && Array.isArray(chat.messages)) {
								trimChatMessages(chat, INACTIVE_KEEP_MESSAGES);
							}
						});
						chatsData.chats = chats;
						context.commit("initChats", chatsData);
						resolve();
					} else if (chatsData.chatKeys) {
						loadChatsInBatches(chatsData.chatKeys).then((chats) => {
							chatsData.chats = chats;
							context.commit("initChats", chatsData);
							resolve();
						}).catch(() => {
							console.log("加载消息失败")
							reject();
						})
					} else {
						resolve();
					}
				}).catch(() => {
					console.log("加载消息失败")
					reject();
				})
			})
		}
	},
	getters: {
		isLocalDataCorrupted: (state) => () => {
			return !!state.localDataCorrupted;
		},
		isLoading: (state) => () => {
			return state.loading;
		},
		isHiddenPrivateChat: (state) => (friendId) => {
			return isHiddenPrivateId(state, friendId);
		},
		findChats: (state, getters) => () => {
			if (cacheChats && getters.isLoading()) {
				return cacheChats;
			}
			return state.chats;
		},
		findChatIdx: (state, getters) => (chat) => {
			const found = getters.findChat(chat);
			if (!found) {
				return;
			}
			return getters.findChats().indexOf(found);
		},
		findChat: (state, getters) => (chat) => {
			const found = lookupChat(chat);
			if (found) {
				return found;
			}
			if (!chat) {
				return;
			}
			const chats = getters.findChats();
			if (!Array.isArray(chats)) {
				return;
			}
			return chats.find((item) => item && item.type == chat.type && item.targetId == chat.targetId);
		},
		findChatByFriend: (state, getters) => (fid) => {
			return getters.findChat({ type: 'PRIVATE', targetId: fid });
		},
		findChatByGroup: (state, getters) => (gid) => {
			return getters.findChat({ type: 'GROUP', targetId: gid });
		},
		messageBelongsToChat: () => (chat, msg) => {
			return privateMessageBelongs(chat, msg);
		},
		findMessage: (state) => (chat, msgInfo) => {
			if (!chat || !Array.isArray(chat.messages)) {
				return null;
			}
			if (chat.type == 'SYSTEM') {
				if (!msgInfo.seqNo || msgInfo.seqNo <= 0) {
					return null;
				}
				for (let idx = 0; idx < chat.messages.length; idx++) {
					if (chat.messages[idx] && chat.messages[idx].seqNo == msgInfo.seqNo) {
						return chat.messages[idx];
					}
				}
			} else {
				if (msgInfo.id && msgInfo.id > 0) {
					for (let idx = 0; idx < chat.messages.length; idx++) {
						if (chat.messages[idx] && chat.messages[idx].id == msgInfo.id) {
							return chat.messages[idx];
						}
					}
				}
				if (msgInfo.tmpId) {
					for (let idx = 0; idx < chat.messages.length; idx++) {
						if (chat.messages[idx] && chat.messages[idx].tmpId && chat.messages[idx].tmpId == msgInfo.tmpId) {
							return chat.messages[idx];
						}
					}
				}
			}
			return null;
		}
	}
}
